(() => {
  const tag = 'blvck-collection-more';
  if (customElements.get(tag)) return;
  const keyFor = () => {
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return url.href;
  };

  class CollectionMore extends HTMLElement {
    connectedCallback() {
      this.grid = this.closest('[data-products-grid]')?.querySelector('#CollectionLoop');
      this.link = this.querySelector('[data-more-link]');
      this.label = this.querySelector('[data-more-label]');
      this.count = this.querySelector('[data-more-count]');
      this.status = this.querySelector('[data-more-status]');
      if (!this.grid || !this.link) return;
      this.idleLabel = this.label.textContent;
      this.key = keyFor();
      this.pages = [];
      this.loading = false;
      this.failed = false;
      this.onClick = (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.failed = false;
        this.loadMore(true);
      };
      this.onGridClick = (event) => {
        const link = event.target.closest('a[href]');
        const item = link?.closest('[data-grid-item]');
        if (!item || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        this.anchor = {id: item.id, offset: item.getBoundingClientRect().top};
        this.remember();
      };
      this.onPageHide = () => {
        this.remember();
        history.scrollRestoration = 'auto';
      };
      this.link.addEventListener('click', this.onClick);
      this.grid.addEventListener('click', this.onGridClick, true);
      window.addEventListener('pagehide', this.onPageHide);
      this.refreshCount();
      this.start();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.link?.removeEventListener('click', this.onClick);
      this.grid?.removeEventListener('click', this.onGridClick, true);
      window.removeEventListener('pagehide', this.onPageHide);
    }

    async start() {
      let saved = history.state?.blvckCollection;
      const navigationType = performance.getEntriesByType('navigation')[0]?.type;
      if (!saved && ['back_forward', 'reload'].includes(navigationType)) {
        try {
          const backup = JSON.parse(sessionStorage.getItem('blvck:collection-position'));
          if (backup?.key === this.key && Date.now() - backup.savedAt < 1800000) saved = backup;
        } catch (error) {}
      }
      if (saved?.key === this.key && Array.isArray(saved.pages) && saved.pages.length) {
        this.restoring = true;
        this.anchor = saved.anchor;
        history.scrollRestoration = 'manual';
        for (let i = 0; i < Math.min(saved.pages.length, 30); i++) {
          if (!this.isConnected || this.link.hidden || !await this.loadMore(false)) break;
        }
        this.restoring = false;
        if (this.isConnected && keyFor() === this.key) {
          requestAnimationFrame(() => {
            const anchor = this.anchor && document.getElementById(this.anchor.id);
            const top = anchor ? window.scrollY + anchor.getBoundingClientRect().top - this.anchor.offset : saved.scrollY || 0;
            window.scrollTo({top, behavior: 'instant'});
          });
        }
      }
      if (!this.isConnected || this.dataset.mode !== 'auto' || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !this.failed && document.activeElement !== this.link) {
          this.loadMore(false);
        }
      }, {rootMargin: '0px 0px 320px 0px'});
      if (!this.link.hidden) this.observer.observe(this.link);
    }

    refreshCount() {
      const shown = Number(this.dataset.offset || 0) + this.grid.querySelectorAll('[data-grid-item]').length;
      this.count.textContent = this.dataset.countText.replace('[count]', shown).replace('[total]', this.dataset.total);
      return this.count.textContent;
    }

    remember() {
      if (!this.isConnected || keyFor() !== this.key || this.restoring || !this.pages.length) return;
      const position = {key: this.key, pages: this.pages, scrollY: window.scrollY, anchor: this.anchor, savedAt: Date.now()};
      history.replaceState({...history.state, blvckCollection: position}, '', location.href);
      try {
        sessionStorage.setItem('blvck:collection-position', JSON.stringify(position));
      } catch (error) {}
    }

    async loadMore(manual) {
      if (this.loading || this.link.hidden || !this.isConnected || keyFor() !== this.key) return false;
      const url = new URL(this.link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return false;
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const pageUrl = url.href;
      if (this.pages.includes(pageUrl)) return false;
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.loading = true;
      this.controller = new AbortController();
      this.link.setAttribute('aria-disabled', 'true');
      this.grid.setAttribute('aria-busy', 'true');
      this.label.textContent = this.dataset.loadingText;
      this.status.classList.add('visually-hidden');
      this.status.textContent = this.dataset.loadingText;
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const response = await fetch(url, {signal: this.controller.signal, credentials: 'same-origin'});
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = html.querySelector('#CollectionLoop');
        const nextPager = html.querySelector(tag);
        if (!nextGrid || !nextPager) throw new Error('Collection content missing');
        if (!this.isConnected || keyFor() !== this.key) return false;
        const existing = new Set(Array.from(this.grid.querySelectorAll('[data-grid-item]'), (item) => item.id));
        const items = Array.from(nextGrid.querySelectorAll(':scope > [data-grid-item]')).filter((item) => !existing.has(item.id));
        if (!items.length) throw new Error('No additional products');
        const fragment = document.createDocumentFragment();
        for (const item of items) {
          item.querySelectorAll('img').forEach((img) => {
            img.loading = 'lazy';
            img.removeAttribute('fetchpriority');
          });
          fragment.append(item);
        }
        this.grid.append(fragment);
        this.pages.push(pageUrl);
        this.dataset.total = nextPager.dataset.total;
        const nextLink = nextPager.querySelector('[data-more-link]');
        this.link.hidden = !nextLink || nextLink.hasAttribute('hidden');
        if (!this.link.hidden) this.link.href = new URL(nextLink.getAttribute('href'), location.href).href;
        else this.observer?.disconnect();
        this.failed = false;
        this.status.textContent = this.refreshCount();
        if (manual) {
          const firstLink = items[0].querySelector('a[href]');
          firstLink?.focus({preventScroll: true});
          items[0].scrollIntoView({block: 'start', behavior: 'instant'});
        }
        this.remember();
        return true;
      } catch (error) {
        if (this.isConnected && keyFor() === this.key) {
          this.failed = true;
          this.status.classList.remove('visually-hidden');
          this.status.textContent = this.dataset.errorText;
        }
        return false;
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        this.link.removeAttribute('aria-disabled');
        this.grid.removeAttribute('aria-busy');
        this.label.textContent = this.idleLabel;
      }
    }
  }
  customElements.define(tag, CollectionMore);
})();
